import React from "react";
import styled from "styled-components";
import { style } from "../style";

type BaseButtonProps = {
  $invalid: boolean;
  $borderRadiusBottomLeft: "none" | "base";
  $borderRadiusBottomRight: "none" | "base";
  $borderRadiusTopLeft: "none" | "base";
  $borderRadiusTopRight: "none" | "base";
  $borderTop: boolean;
  $borderBottom: boolean;
  $borderLeft: boolean;
  $borderRight: boolean;
  $padding: string;
};

const InnerButton = styled.button<BaseButtonProps>`
  all: unset;

  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};

  ${(props) => {
    return {
      borderTopWidth: props.$borderTop ? style.vars.borderWidth.thin : 0,
      borderBottomWidth: props.$borderBottom ? style.vars.borderWidth.thin : 0,
      borderLeftWidth: props.$borderLeft ? style.vars.borderWidth.thin : 0,
      borderRightWidth: props.$borderRight ? style.vars.borderWidth.thin : 0,
      borderTopLeftRadius: props.$borderRadiusTopLeft === "base" ? style.vars.borderRadius.base : 0,
      borderTopRightRadius: props.$borderRadiusTopRight === "base" ? style.vars.borderRadius.base : 0,
      borderBottomLeftRadius: props.$borderRadiusBottomLeft === "base" ? style.vars.borderRadius.base : 0,
      borderBottomRightRadius: props.$borderRadiusBottomRight === "base" ? style.vars.borderRadius.base : 0,
      borderColor: props.$invalid ? style.colors.red.dark : undefined,
      padding: props.$padding,
    };
  }}

  cursor: pointer;
  color: ${style.colors.grey.dark};

  background-color: ${style.colors.white};

  :hover:not(:disabled) {
    background-color: ${style.colors.grey.lightest};
  }

  :active:not(:disabled) {
    color: ${style.colors.blue.dark};
    border-color: ${style.colors.blue.dark};
    background-color: ${style.colors.blue.lightest};
  }

  :focus:not(:disabled) {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: ${style.vars.borderWidth.thin};
    z-index: ${style.vars.zIndex.raised};
  }

  :disabled {
    cursor: unset;
    opacity: 0.3;
    outline: none;
  }
`;

type Props = React.PropsWithChildren<{
  borderTop?: boolean;
  borderBottom?: boolean;
  borderLeft?: boolean;
  borderRight?: boolean;
  borderRadiusTopLeft?: "none" | "base";
  borderRadiusTopRight?: "none" | "base";
  borderRadiusBottomLeft?: "none" | "base";
  borderRadiusBottomRight?: "none" | "base";
  as?: keyof JSX.IntrinsicElements;
  paddingX?: string;
  paddingY?: string;
  invalid?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseUp?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
}>;

export const LooseInteractionButton = React.forwardRef<HTMLButtonElement, Props>((props, forwardedRef) => {
  const {
    borderTop = true,
    borderBottom = true,
    borderLeft = true,
    borderRight = true,
    borderRadiusTopLeft = "none",
    borderRadiusTopRight = "none",
    borderRadiusBottomLeft = "none",
    borderRadiusBottomRight = "none",
    paddingX = `0`,
    paddingY = `0`,
    invalid = false,
    disabled = false,
    as,
    onFocus,
    onBlur,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    children,
  } = props;

  return (
    <InnerButton
      as={as}
      role="button"
      ref={forwardedRef as React.RefObject<HTMLButtonElement>}
      data-testid={props["data-testid"]}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      $borderTop={borderTop}
      $borderBottom={borderBottom}
      $borderLeft={borderLeft}
      $borderRight={borderRight}
      $borderRadiusTopLeft={borderRadiusTopLeft}
      $borderRadiusTopRight={borderRadiusTopRight}
      $borderRadiusBottomLeft={borderRadiusBottomLeft}
      $borderRadiusBottomRight={borderRadiusBottomRight}
      $invalid={invalid}
      $padding={`${paddingY} ${paddingX}`}
    >
      {children}
    </InnerButton>
  );
});
